import { CartComponentBase } from '@utilities/cart-component-base';
import { EVENTS } from '@utilities/events';

if (!customElements.get("cart-note")) {
  /**
   * CartNote Custom Web Component
   *
   * Manages gift message functionality in the shopping cart, providing interfaces for:
   * - Adding gift messages with recipient, sender, and message content
   * - Choosing between message cards and blank cards
   * - Form validation and character/line break limits
   * - State management between default, edit, and completed views
   * - Integration with Shopify cart attributes
   * - Uses CartSubDrawer for form UI presentation
   *
   * @class CartNote
   * @extends CartComponentBase
   */
  class CartNote extends CartComponentBase {
    /**
     * CartSubDrawer instance for displaying the gift note form
     * @type {CartSubDrawer|null}
     * @private
     */
    #subDrawer = null;

    /**
     * Flag to track when user is actively interacting with the component
     * Prevents cart state updates from interfering with user actions
     * @type {boolean}
     * @private
     */
    #isUserInteracting = false;

    /**
     * Cart state containing renderCartNote flag
     * @type {Object|null}
     * @private
     */
    #cartState = null;

    /**
     * Creates an instance of CartNote
     * Initializes component state, storage keys, and default message data
     *
     * @constructor
     */
    constructor() {
      super();

      /**
       * Component view states
       * @type {Object}
       * @property {boolean} DEFAULT_VIEW - Initial state showing "add gift message" option
       * @property {boolean} EDIT_VIEW - Form state for editing gift message details
       * @property {boolean} GIFT_MESSAGE_ADDED_VIEW - Confirmation state showing message added
       */
      this.state = {
        DEFAULT_VIEW: true,
        EDIT_VIEW: false,
        GIFT_MESSAGE_ADDED_VIEW: false,
      };

      /**
       * Session storage keys for persisting gift message data
       * @type {Object}
       * @property {string} to - Storage key for recipient name
       * @property {string} from - Storage key for sender name
       * @property {string} message - Storage key for gift message content
       * @property {string} cardType - Storage key for selected card type
       */
      this.storage = {
        to: "greyson_cart_note_to",
        from: "greyson_cart_note_from",
        message: "greyson_cart_note_message",
        cardType: "greyson_cart_note_card_type",
      };

      /**
       * Gift message recipient name
       * @type {string}
       */
      this.messageTo = "";

      /**
       * Gift message sender name
       * @type {string}
       */
      this.messageFrom = "";

      /**
       * Gift message content
       * @type {string}
       */
      this.messageMessage = "";

      /**
       * Selected card type for the gift message
       * @type {string}
       * @default 'message-card'
       */
      this.selectedCardType = "message-card"; // Default to message card
    }

    /**
     * Clean up when component is removed from DOM
     */
    disconnectedCallback() {
      super.disconnectedCallback();
      this.#destroySubDrawer();
    }

    /**
     * Override the base class component-specific initialization
     * @protected
     */
    async _initializeComponent() {
      this.#initElements();
      this.#initMessageData();
      this.#initEventListeners();
      this.#initState();

      // Get initial state from state manager
      this.#updateFromState();
    }

    /**
     * Override the base class event listener setup
     * @protected
     */
    _setupEventListeners() {
      this._subscribe(EVENTS.CART_STATE_CHANGED, this.#handleStateUpdate.bind(this));
      this._subscribe(EVENTS.CART_STATE_REFRESHED, this.#handleStateUpdate.bind(this));
    }

    /**
     * Update component state from cart attributes
     * @param {Object} attributes - Cart attributes
     */
    updateFromCartAttributes(attributes) {
      // Extract gift message attributes
      const cartGiftMessage = {
        to: attributes.gift_message_to || "",
        from: attributes.gift_message_from || "",
        message: attributes.gift_message || "",
        isBlankCard: !!attributes.gift_message_blank_card
      };

      const hasGiftMessage = cartGiftMessage.to || cartGiftMessage.from || cartGiftMessage.message || cartGiftMessage.isBlankCard;

      if (this.allowDebug()) {
        console.log("CartNote: Updating from cart attributes", {
          attributes,
          cartGiftMessage,
          hasGiftMessage,
          currentState: this.state,
          currentProperties: {
            to: this.messageTo,
            from: this.messageFrom,
            message: this.messageMessage,
            cardType: this.selectedCardType
          }
        });
      }

      // Check if attributes have actually changed
      const hasChanged =
        this.messageTo !== cartGiftMessage.to ||
        this.messageFrom !== cartGiftMessage.from ||
        this.messageMessage !== cartGiftMessage.message ||
        this.selectedCardType !== (cartGiftMessage.isBlankCard ? "blank-card" : "message-card");

      if (!hasChanged) {
        if (this.allowDebug()) {
          console.log("CartNote: No changes detected in cart attributes, skipping update");
        }
        return;
      }

      if (hasGiftMessage) {
        // Update component properties and session storage
        this.messageTo = cartGiftMessage.to;
        this.messageFrom = cartGiftMessage.from;
        this.messageMessage = cartGiftMessage.message;
        this.selectedCardType = cartGiftMessage.isBlankCard ? "blank-card" : "message-card";

        // Update session storage
        if (cartGiftMessage.to) {
          sessionStorage.setItem(this.storage.to, cartGiftMessage.to);
        } else {
          sessionStorage.removeItem(this.storage.to);
        }

        if (cartGiftMessage.from) {
          sessionStorage.setItem(this.storage.from, cartGiftMessage.from);
        } else {
          sessionStorage.removeItem(this.storage.from);
        }

        if (cartGiftMessage.message) {
          sessionStorage.setItem(this.storage.message, cartGiftMessage.message);
        } else {
          sessionStorage.removeItem(this.storage.message);
        }

        sessionStorage.setItem(this.storage.cardType, this.selectedCardType);

        // Only update UI state if we are not currently in an interactive state
        // This prevents state conflicts during user interactions
        if (!this.state.EDIT_VIEW && !this.#isUserInteracting) {
          this.#initState();
        }
      } else {
        // No gift message in cart - clear component state
        this.messageTo = "";
        this.messageFrom = "";
        this.messageMessage = "";
        this.selectedCardType = "message-card";

        // Clear session storage
        Object.values(this.storage).forEach(key => sessionStorage.removeItem(key));

        // Reset to default view if not editing
        if (!this.state.EDIT_VIEW && !this.#isUserInteracting) {
          this.#transitionState({ DEFAULT_VIEW: true });
        }
      }
    }

    /**
     * Handle state updates from the state manager
     * Handles both regular state changes and refresh events
     * Updates both visibility and gift message attributes
     * @param {CustomEvent} event - State updated event
     * @private
     */
    #handleStateUpdate(data) {
      const { state } = data;

      if (this.allowDebug()) {
        console.log("CartNote: Received state update", {
          hasState: !!state,
          renderCartNote: state?.renderCartNote,
          hasAttributes: !!state?.attributes
        });
      }

      // Update gift message data from cart attributes if present
      if (state?.attributes) {
        this.updateFromCartAttributes(state.attributes);
      }

      // Update visibility from the latest state
      this.#updateFromState();
    }

    /**
     * Update component state from current cart state in state manager
     * @private
     */
    #updateFromState() {
      // Get cart state from state manager
      const cartState = this.getCartState();

      if (!cartState) {
        if (this.allowDebug()) {
          console.log("CartNote: No state available from state manager, defaulting to visible");
        }
        // Default to visible if state is not available
        this.classList.remove('hidden');
        this.removeAttribute('hidden');
        return;
      }

      if (this.allowDebug()) {
        console.log("CartNote: Processing cart state from state manager", {
          renderCartNote: cartState.renderCartNote,
          currentlyHidden: this.hasAttribute('hidden') || this.classList.contains('hidden')
        });
      }

      // Update our state reference
      this.#cartState = cartState;

      // Update component visibility based on renderCartNote flag
      this.#updateVisibility();
    }

    /**
     * Update component visibility based on renderCartNote flag
     * @private
     */
    #updateVisibility() {
      if (!this.#cartState) {
        if (this.allowDebug()) {
          console.warn("CartNote: #updateVisibility called without cart state");
        }
        return;
      }

      const shouldRender = this.#cartState.renderCartNote;

      if (this.allowDebug()) {
        console.log("CartNote: Updating visibility", {
          renderCartNote: shouldRender,
          currentlyHidden: this.hasAttribute('hidden') || this.classList.contains('hidden')
        });
      }

      if (shouldRender) {
        // Show the component
        this.classList.remove('hidden');
        this.removeAttribute('hidden');
      } else {
        // Hide the component
        this.classList.add('hidden');
      }
    }

    /**
     * Initializes DOM element references and clones template content
     * Sets up references to cart drawer, form, and template elements
     * @private
     */
    #initElements() {
      this.cartDrawer = this.closest("cart-drawer");
      this.cartForm = this.cartDrawer?.querySelector('form[name="cart"]');

      const getTemplate = (id) =>
        this.querySelector(`#${id}`)?.content.firstElementChild.cloneNode(true);
      this.sendingAGift = getTemplate("sending-a-gift-template");
      this.giftMessageAdded = getTemplate("gift-message-added-template");
      this.editMessage = getTemplate("edit-message-template");
    }

    /**
     * Checks if minimum requirements for a gift message are met
     * For blank cards, no content is required. For message cards, at least one field must have content.
     *
     * @returns {boolean} True if requirements are met, false otherwise
     * @private
     */
    #areMessageRequirementsMet() {
      return (
        this.selectedCardType === "blank-card" ||
        [this.messageTo, this.messageFrom, this.messageMessage].some(
          (msg) => msg !== ""
        )
      );
    }

    /**
     * Populates form fields with stored message data from session storage
     * Also handles radio button state and updates character/line break counters
     * @private
     */
    #insertMessageData() {
      // Determine the context (main component or sub-drawer)
      const context = this.#subDrawer?.content || this;

      const updateElementValue = (name, storageKey) => {
        const element = context.querySelector(`[name="cart-note-${name}"]`);
        const storedValue = sessionStorage.getItem(storageKey);
        if (element && storedValue) {
          element.value = storedValue;
        }
      };

      updateElementValue("to", this.storage.to);
      updateElementValue("from", this.storage.from);
      updateElementValue("message", this.storage.message);

      // Set the radio button state
      const storedCardType = sessionStorage.getItem(this.storage.cardType);
      if (storedCardType) {
        this.selectedCardType = storedCardType;
        const radioInput = context.querySelector(
          `input[name="cart-note-card-type"][value="${storedCardType}"]`
        );
        if (radioInput) {
          radioInput.checked = true;
        }
      }

      // Ensure textarea has proper maxlength from config
      const messageNoteInput = context.querySelector(
        '.cart-note__input[name="cart-note-message"]'
      );
      if (messageNoteInput && this.config?.maxCharacters) {
        messageNoteInput.setAttribute('maxlength', this.config?.maxCharacters);
      }

      // Update limited values for message input based on current content
      this.#updateMaxCharacterCount(messageNoteInput);
      this.#limitLineBreaks(messageNoteInput);
    }

    /**
     * Updates component properties and session storage with current form values
     * Syncs form data to component state and persistent storage
     * @private
     */
    #updateMessageData() {
      // Determine the context (main component or sub-drawer)
      const context = this.#subDrawer?.content || this;

      const updateStorageItem = (name, storageKey) => {
        const element = context.querySelector(`[name="cart-note-${name}"]`);
        if (element?.value) {
          this[`message${name.charAt(0).toUpperCase() + name.slice(1)}`] =
            element.value;
          sessionStorage.setItem(storageKey, element.value);
        } else {
          this[`message${name.charAt(0).toUpperCase() + name.slice(1)}`] = "";
          sessionStorage.removeItem(storageKey);
          if (element) element.value = "";
        }
      };

      updateStorageItem("to", this.storage.to);
      updateStorageItem("from", this.storage.from);
      updateStorageItem("message", this.storage.message);

      // Update the radio button state
      const selectedRadio = context.querySelector(
        'input[name="cart-note-card-type"]:checked'
      );
      if (selectedRadio) {
        this.selectedCardType = selectedRadio.value;
        sessionStorage.setItem(this.storage.cardType, this.selectedCardType);
      }
    }

    /**
     * Initializes message data properties from session storage
     * Loads previously saved gift message data if available
     * @private
     */
    #initMessageData() {
      ["To", "From", "Message"].forEach((field) => {
        const storageKey = `greyson_cart_note_${field.toLowerCase()}`;
        this[`message${field}`] = sessionStorage.getItem(storageKey) || "";
      });
    }

    /**
     * Sets up event listeners based on current component state
     * Delegates to specific listener initialization methods per state
     * @private
     */
    #initEventListeners() {
      if (this.state.DEFAULT_VIEW) {
        this.querySelector(".cart-note__add-message")?.addEventListener(
          "click",
          () => this.#transitionState({ EDIT_VIEW: true })
        );
      } else if (this.state.GIFT_MESSAGE_ADDED_VIEW) {
        this.querySelector(".cart-note__edit-message")?.addEventListener(
          "click",
          () => this.#transitionState({ EDIT_VIEW: true })
        );
      }
      // EDIT_VIEW now uses sub-drawer with its own event listener setup
    }

    /**
     * Resets the gift message by clearing cart attributes
     * @private
     */
    async #resetGiftMessage() {
      // Set interaction flag to prevent state conflicts during reset
      this.#isUserInteracting = true;

      // Prepare cart attributes with empty strings to clear them
      const attributes = {
        gift_message_to: "",
        gift_message_from: "",
        gift_message: "",
        gift_message_blank_card: "",
      };

      if (this.allowDebug()) {
        console.log("CartNote: Resetting gift message", { attributes });
      }

      try {
        // Clear the textarea in the sub-drawer if it exists
        const subDrawerContent = this.#subDrawer?.content;
        if (subDrawerContent) {
          const messageNoteInput = subDrawerContent.querySelector('.cart-note__input[name="cart-note-message"]');
          if (messageNoteInput) {
            messageNoteInput.value = '';
          }

          // Clear all form inputs
          const allInputs = subDrawerContent.querySelectorAll('.cart-note__input');
          allInputs.forEach(input => {
            input.value = '';
          });
        }

        // Use state manager to update cart attributes with empty values
        const stateManager = this.cartStateManager;
        if (!stateManager) {
          throw new Error("CartNote: CartStateManager not available for updating attributes");
        }

        await stateManager.updateAttributes(attributes);

        if (this.allowDebug()) {
          console.log("CartNote: Gift message reset successfully");
        }

        // Clear component state and session storage
        this.messageTo = "";
        this.messageFrom = "";
        this.messageMessage = "";
        this.selectedCardType = "message-card";

        // Clear session storage
        Object.values(this.storage).forEach(key => sessionStorage.removeItem(key));

        // Close sub-drawer and transition to default state
        this.#closeSubDrawer();
        this.#transitionState({ DEFAULT_VIEW: true });
      } catch (error) {
        this._handleError(error, 'resetting cart attributes', true);

        // Close sub-drawer and transition to default state even on error
        this.#closeSubDrawer();
        this.#transitionState({ DEFAULT_VIEW: true });
      } finally {
        // Clear interaction flag after reset operation completes
        this.#isUserInteracting = false;
      }
    }

    /**
     * Saves the gift message to cart attributes using state manager
     * @private
     */
    async #saveGiftMessage() {
      // Set interaction flag to prevent state conflicts during save
      this.#isUserInteracting = true;

      // Get the selected card type
      const isBlankCard = this.selectedCardType === "blank-card";

      // Prepare cart attributes with the gift message
      const attributes = {
        gift_message_to: isBlankCard ? "" : this.messageTo,
        gift_message_from: isBlankCard ? "" : this.messageFrom,
        gift_message: isBlankCard ? "" : this.messageMessage,
        gift_message_blank_card: isBlankCard ? true : "",
      };

      if (this.allowDebug()) {
        console.log("CartNote: Saving gift message", { isBlankCard, attributes });
      }

      try {
        // Use state manager to update cart attributes
        const stateManager = this.cartStateManager;
        if (!stateManager) {
          throw new Error("CartNote: CartStateManager not available for updating attributes");
        }

        await stateManager.updateAttributes(attributes);

        if (this.allowDebug()) {
          console.log("CartNote: Gift message saved successfully");
        }

        // Close sub-drawer and transition to appropriate state
        this.#closeSubDrawer();
        this.#transitionState({
          [this.#areMessageRequirementsMet()
            ? "GIFT_MESSAGE_ADDED_VIEW"
            : "DEFAULT_VIEW"]: true,
        });
      } catch (error) {
        this._handleError(error, 'updating cart attributes', true);

        // Close sub-drawer and transition to appropriate state even on error
        this.#closeSubDrawer();
        this.#transitionState({
          [this.#areMessageRequirementsMet()
            ? "GIFT_MESSAGE_ADDED_VIEW"
            : "DEFAULT_VIEW"]: true,
        });
      } finally {
        // Clear interaction flag after save operation completes
        this.#isUserInteracting = false;
      }
    }

    /**
     * Validates and removes invalid characters from form inputs
     * Shows/hides error messages based on validation results
     *
     * @param {HTMLFormElement} messageForm - The form containing allowed characters data
     * @param {HTMLInputElement} formInput - The input element to validate
     * @private
     */
    #removeInvalidCharacters(messageForm, formInput) {
      const inputValue = formInput.value;

      const escapeRegExp = (string) => {
        // First unescape any escaped characters, then escape all characters properly
        // Handle hyphens separately to avoid range interpretation
        return string
          .replace(/\\([.*+?^${}()|[\]\\])/g, "$1")
          .replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
          .replace(/(?<!\\)-/g, "\\-");
      };

      // Get the allowed characters from config instead of data attribute
      const allowedChars = this.config?.allowedCharacters;
      if (!allowedChars) {
        if (this.allowDebug()) {
          console.warn("CartNote: No allowed_characters config found");
        }
        return;
      }

      const escapedAllowedChars = escapeRegExp(allowedChars);
      // Always allow spaces in addition to the allowed characters
      const validCharsRegex = new RegExp(`^[${escapedAllowedChars}\\s]*$`);

      // Remove any characters not allowed by the regex
      const hasInvalidCharacters = !validCharsRegex.test(inputValue);
      if (hasInvalidCharacters) {
        formInput.value = inputValue.replace(
          new RegExp(`[^${escapedAllowedChars}\\s]`, "g"),
          ""
        );
      }

      // Update visibility of the prohibited character error message
      // Check both main component and sub-drawer for the error element
      const context = this.#subDrawer?.isOpen ? this.#subDrawer : this;
      const prohibitedCharacterError = context.querySelector(
        ".cart-note__alerts-error"
      );
      prohibitedCharacterError?.toggleAttribute("hidden", !hasInvalidCharacters);
    }

    /**
     * Counts the number of lines in a given text string
     *
     * @param {string} text - Text to analyze for lines
     * @returns {number} Number of lines found
     * @private
     */
    #countLines(text) {
      // Handle empty text as 0 lines for counting purposes
      if (text === '') {
        return 0;
      }
      // Split by line breaks and count the resulting array length
      // This gives us the actual number of lines, not line breaks
      return text.split('\n').length;
    }

    /**
     * Enforces line limits on form inputs and updates counter display
     * Removes excess lines and updates the remaining line count
     *
     * @param {HTMLInputElement} formInput - Input element with line break limit data attribute
     * @private
     */
    #limitLineBreaks(formInput) {
      if (!formInput) {
        if (this.allowDebug()) {
          console.warn("CartNote: #limitLineBreaks called with no formInput");
        }
        return;
      }

      // Get line break limit from config instead of data attribute
      const lineBreakLimit = parseInt(this.config?.lineBreakLimit);
      if (Number.isNaN(lineBreakLimit)) {
        if (this.allowDebug()) {
          console.warn("CartNote: Invalid line break limit", {
            configValue: this.config?.lineBreakLimit,
            formInput: formInput.name
          });
        }
        return;
      }

      const currentLines = this.#countLines(formInput.value);

      // Check if we've exceeded the line limit
      if (currentLines > lineBreakLimit) {
        // Split into lines and keep only the allowed number
        const lines = formInput.value.split('\n');
        formInput.value = lines.slice(0, lineBreakLimit).join('\n');
      }

      // Determine the context (main component or sub-drawer)
      const context = this.#subDrawer?.content || this;
      const lineBreakLimitCount = context.querySelector(
        ".cart-note__alerts-limit-count"
      );

      if (this.allowDebug()) {
        console.log("CartNote: Updating line count", {
          formInput: formInput.name,
          currentLines: this.#countLines(formInput.value),
          lineBreakLimit: lineBreakLimit,
          remaining: lineBreakLimit - this.#countLines(formInput.value),
          lineBreakCountElement: !!lineBreakLimitCount,
          context: this.#subDrawer?.content ? 'sub-drawer' : 'main'
        });
      }

      if (lineBreakLimitCount) {
        const remaining = lineBreakLimit - this.#countLines(formInput.value);

        // Get template message from config instead of data attribute
        const templateMessage = this.config?.messageErrorLineBreakLimit;

        if (this.allowDebug()) {
          console.log("CartNote: Processing line break template", {
            remaining,
            templateMessage,
            currentContent: lineBreakLimitCount.textContent,
            configValue: this.config?.messageErrorLineBreakLimit
          });
        }

        if (templateMessage) {
          // Use template with placeholder replacement
          const lineText = remaining === 1 ? 'line' : 'lines';
          const newContent = templateMessage
            .replace('{count}', remaining)
            .replace('{line_count}', remaining)
            .replace('{lines}', lineText);

          if (this.allowDebug()) {
            console.log("CartNote: Template processed", {
              template: templateMessage,
              processed: newContent
            });
          }

          lineBreakLimitCount.textContent = newContent;
        } else {
          // Fallback to default dynamic message
          const lineText = remaining === 1 ? 'line' : 'lines';
          const fallbackContent = `${remaining} ${lineText} remaining`;

          if (this.allowDebug()) {
            console.log("CartNote: Using fallback message", { fallbackContent });
          }

          lineBreakLimitCount.textContent = fallbackContent;
        }
      } else if (this.allowDebug()) {
        console.warn("CartNote: Line break count element not found");
      }
    }

    /**
     * Updates the character count display for form inputs
     * Shows current character count vs maximum allowed characters
     *
     * @param {HTMLInputElement} formInput - Input element with maxlength attribute
     * @private
     */
    #updateMaxCharacterCount(formInput) {
      if (!formInput) return;

      // Determine the context (main component or sub-drawer)
      const context = this.#subDrawer?.content || this;
      const messageCount = context.querySelector(".cart-note__input-count");

      // Get max length from config instead of form attribute
      const maxLength = this.config?.maxCharacters;

      if (this.allowDebug()) {
        console.log("CartNote: Updating character count", {
          formInput: formInput.name,
          currentLength: formInput.value.length,
          maxLength: maxLength,
          messageCountElement: !!messageCount,
          context: this.#subDrawer?.content ? 'sub-drawer' : 'main'
        });
      }

      if (messageCount && maxLength) {
        const currentLength = formInput.value.length;
        messageCount.textContent = `${currentLength}/${maxLength}`;
      } else if (this.allowDebug()) {
        console.warn("CartNote: Character count element not found or maxCharacters config missing");
      }
    }

    /**
     * Initializes the component's initial state based on existing message data
     * Determines whether to show default view or gift message added view
     * @private
     */
    #initState() {
      this.#transitionState({
        [this.#areMessageRequirementsMet()
          ? "GIFT_MESSAGE_ADDED_VIEW"
          : "DEFAULT_VIEW"]: true,
      });
    }

    /**
     * Transitions the component to a new state
     * Updates state object and triggers UI re-render
     *
     * @param {Object} newState - Object with state key set to true for desired state
     * @private
     */
    #transitionState(newState) {
      this.state = Object.fromEntries(
        Object.keys(this.state).map((key) => [key, newState[key] || false])
      );
      this.#setState();
    }

    /**
     * Renders the component UI based on current state
     * Manages DOM content and sub-drawer visibility
     * @private
     */
      #setState() {
    const currentState =
      Object.keys(this.state).find((key) => this.state[key]) ||
      "DEFAULT_VIEW";

    if (this.allowDebug()) {
      console.log("CartNote: Setting state to", currentState);
    }

    // Close any existing sub-drawer
    this.#closeSubDrawer();

    if (currentState === "DEFAULT_VIEW") {
      this.innerHTML = this.sendingAGift.innerHTML;
    } else if (currentState === "GIFT_MESSAGE_ADDED_VIEW") {
      this.innerHTML = this.giftMessageAdded.innerHTML;
    } else if (currentState === "EDIT_VIEW") {
      // Show the sub-drawer with the gift note form
      this.#showSubDrawer();
      return; // Don't set innerHTML for EDIT_VIEW, sub-drawer handles it
    }

    this.#initEventListeners();
  }

    /**
     * Updates template content with config values
     * @param {string} templateHTML - The original template HTML
     * @returns {string} Updated HTML with config values
     * @private
     */
    #updateTemplateWithConfig(templateHTML) {
      let updatedHTML = templateHTML;

      // Replace form attributes
      updatedHTML = updatedHTML.replace(
        /data-allowed-characters="[^"]*"/g,
        `data-allowed-characters="${this.config?.allowedCharacters || ''}"`
      );

      // Replace maxlength attribute
      updatedHTML = updatedHTML.replace(
        /maxlength="[^"]*"/g,
        `maxlength="${this.config?.maxCharacters || 250}"`
      );

      // Replace data-line-break-limit attribute
      updatedHTML = updatedHTML.replace(
        /data-line-break-limit="[^"]*"/g,
        `data-line-break-limit="${this.config?.lineBreakLimit || 5}"`
      );

      // Replace placeholder text
      if (this.config?.messagePlaceholder) {
        const maxChars = this.config?.maxCharacters || 250;
        const placeholder = this.config?.messagePlaceholder.replace('{max_characters}', maxChars);
        updatedHTML = updatedHTML.replace(
          /placeholder="[^"]*"/g,
          `placeholder="${placeholder}"`
        );
      }

      // Replace data-template attribute for line break messages
      if (this.config?.messageErrorLineBreakLimit) {
        updatedHTML = updatedHTML.replace(
          /data-template="[^"]*"/g,
          `data-template="${this.config?.messageErrorLineBreakLimit}"`
        );
      }

      // Replace character count display
      const maxChars = parseInt(this.config?.maxCharacters || 250, 10);
      updatedHTML = updatedHTML.replace(
        /0\/\d+/g,
        `0/${maxChars}`
      );

      // Replace line break limit display
      const lineBreakLimit = parseInt(this.config?.lineBreakLimit || 5, 10);
      const lineText = lineBreakLimit === 1 ? 'line' : 'lines';
      updatedHTML = updatedHTML.replace(
        /\d+\s+lines?\s+remaining/g,
        `${lineBreakLimit} ${lineText} remaining`
      );

      return updatedHTML;
    }

    /**
     * Creates and shows the sub-drawer with the gift note form
     * @private
     */
    #showSubDrawer() {
      if (this.allowDebug()) {
        console.log("CartNote: Showing sub-drawer");
      }

      // Set interaction flag when entering edit mode
      this.#isUserInteracting = true;

      if (!this.#subDrawer) {
        this.#createSubDrawer();
      }

      // Update template content with config values before setting
      const updatedTemplate = this.#updateTemplateWithConfig(this.editMessage.innerHTML);
      this.#subDrawer.setContent(updatedTemplate);
      this.#insertMessageData();
      this.#initSubDrawerEventListeners();

      // Use requestAnimationFrame to ensure the element is rendered with initial styles
      // before adding the visible class for smooth animation
      requestAnimationFrame(() => {
        this.#subDrawer.show();
      });
    }

    /**
     * Creates a new CartSubDrawer instance
     * @private
     */
    #createSubDrawer() {
      if (this.allowDebug()) {
        console.log("CartNote: Creating sub-drawer");
      }

      this.#subDrawer = document.createElement('cart-sub-drawer');
      this.#subDrawer.setAttribute('aria-label', 'Add gift message');
      this.#subDrawer.id = `cart-note-sub-drawer--${this.id}`;

      // Listen for close events
      this.#subDrawer.addEventListener('sub-drawer:close', () => {
        this.#handleSubDrawerClose();
      });

      // Add to cart drawer
      const cartDrawer = this.closest('cart-drawer');
      if (cartDrawer) {
        cartDrawer.appendChild(this.#subDrawer);
      }
    }

    /**
     * Closes and removes the sub-drawer
     * @private
     */
    #closeSubDrawer() {
      if (this.#subDrawer) {
        if (this.allowDebug()) {
          console.log("CartNote: Closing sub-drawer");
        }
        this.#subDrawer.hide();
      }
    }

    /**
     * Destroys the sub-drawer instance and removes it from DOM
     * @private
     */
    #destroySubDrawer() {
      if (this.#subDrawer) {
        this.#subDrawer.remove();
        this.#subDrawer = null;
      }
    }

    /**
     * Handles sub-drawer close events
     * @private
     */
    #handleSubDrawerClose() {
      // Clear interaction flag when exiting edit mode
      this.#isUserInteracting = false;

      // Return to appropriate state when sub-drawer closes
      this.#transitionState({
        [this.#areMessageRequirementsMet()
          ? "GIFT_MESSAGE_ADDED_VIEW"
          : "DEFAULT_VIEW"]: true,
      });
    }

    /**
     * Initializes event listeners specifically for sub-drawer content
     * @private
     */
    #initSubDrawerEventListeners() {
      if (!this.#subDrawer) return;

      // Get the sub-drawer content area
      const subDrawerContent = this.#subDrawer.content;
      if (!subDrawerContent) return;

      // Form elements
      const messageForm = subDrawerContent.querySelector(".cart-note__form");
      const messageFormInputs = messageForm?.querySelectorAll(".cart-note__input");
      const messageNoteInput = messageForm?.querySelector('.cart-note__input[name="cart-note-message"]');

      // Add input event listeners to all form input elements
      if (this.allowDebug()) {
        console.log("CartNote: Setting up sub-drawer event listeners", {
          messageFormInputs: messageFormInputs?.length || 0,
          messageNoteInput: !!messageNoteInput,
          formElements: Array.from(messageFormInputs || []).map(input => input.name)
        });
      }

      messageFormInputs?.forEach((formInput) => {
        formInput.addEventListener("input", () => {
          if (this.allowDebug()) {
            console.log("CartNote: Input event triggered", {
              inputName: formInput.name,
              value: formInput.value,
              isMessageInput: formInput === messageNoteInput
            });
          }

          this.#removeInvalidCharacters(messageForm, formInput);
          // Special adjustments for message input
          if (formInput === messageNoteInput) {
            this.#limitLineBreaks(formInput);
            this.#updateMaxCharacterCount(formInput);
          }
        });
      });

      // Form submission
      messageForm?.addEventListener("submit", (event) => {
        event.preventDefault();
        this.#updateMessageData();
        this.#saveGiftMessage();
      });

      // Form reset
      messageForm?.addEventListener("reset", (event) => {
        event.preventDefault();
        this.#resetGiftMessage();
      });

      // Back button
      subDrawerContent.querySelector(".cart-note__back")?.addEventListener("click", () => {
        // Close sub-drawer and transition to appropriate state
        this.#closeSubDrawer();
        this.#transitionState({
          [this.#areMessageRequirementsMet()
            ? "GIFT_MESSAGE_ADDED_VIEW"
            : "DEFAULT_VIEW"]: true,
        });
      });
    }


  }

  customElements.define("cart-note", CartNote);
}
